import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { validateLatitude, validateLongitude } from "@utils/formatters";
import { createLocation } from "@services/location/create";
import { globalMutate } from "@utils/swr";
import PhoneInputBR from "@commons/Input/PhoneInput";

const locationSchema = z
  .object({
    code: z.string().min(1, "Código é obrigatório"),
    responsible: z.string().optional(),
    address: z.string().min(1, "Endereço é obrigatório"),
    email: z.string().optional(),
    phone: z.string().min(10, "Telefone é obrigatório"),
    latitude: z
      .string()
      .refine(validateLatitude(), {
        message: "Latitude deve estar entre -90 e 90",
      })
      .optional(),
    longitude: z
      .string()
      .refine(validateLongitude(), {
        message: "Longitude deve estar entre -180 e 180",
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.latitude && !data.longitude) {
      ctx.addIssue({
        code: "custom",
        message: "Longitude é obrigatória quando latitude é informada",
        path: ["longitude"],
      });
    }

    if (!data.latitude && data.longitude) {
      ctx.addIssue({
        code: "custom",
        message: "Latitude é obrigatória quando longitude é informada",
        path: ["latitude"],
      });
    }
  });

type LocationFormData = z.infer<typeof locationSchema>;

function ModalNewLocation() {
  const { isModalOpen, closeModal, setIsLoading } = usePage();
  const open = isModalOpen("newLocation");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LocationFormData>({
    resolver: zodResolver(locationSchema),
    defaultValues: {
      code: "",
      responsible: "",
      address: "",
      email: "",
      phone: "",
      latitude: undefined,
      longitude: undefined,
    },
  });

  const onSubmit = async (data: LocationFormData) => {
    try {
      setIsLoading(true);

      await createLocation({
        code: data.code,
        responsible: data.responsible,
        address: data.address,
        email: data.email,
        phone: data.phone,
        latitude: data.latitude ? Number(data.latitude) : undefined,
        longitude: data.longitude ? Number(data.longitude) : undefined,
      });

      toast.success("Localização criada com sucesso");
      await globalMutate(["fetch-get-locations"]);
      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao criar localização";

      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    closeModal();
    reset();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      heading="Registrar Localização"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-2xl relative"
    >
      <form className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-4">
        <div className="md:col-span-2">
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Código da Localização"
                textSize="sm"
                fullWidth
                error={!!errors.code}
                helperText={errors.code?.message}
              />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Controller
            name="responsible"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Nome dos Responsáveis"
                textSize="sm"
                fullWidth
                error={!!errors.responsible}
                helperText={errors.responsible?.message}
              />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Endereço"
                textSize="sm"
                fullWidth
                error={!!errors.address}
                helperText={errors.address?.message}
              />
            )}
          />
        </div>

        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="E-mail"
              textSize="sm"
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          )}
        />

        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <PhoneInputBR
              value={field.value}
              onChange={field.onChange}
              error={!!errors.phone}
              helperText={errors.phone?.message}
            />
          )}
        />

        <Controller
          name="latitude"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Latitude"
              textSize="sm"
              fullWidth
              type="number"
              error={!!errors.latitude}
              helperText={errors.latitude?.message}
            />
          )}
        />

        <Controller
          name="longitude"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Longitude"
              textSize="sm"
              fullWidth
              type="number"
              error={!!errors.longitude}
              helperText={errors.longitude?.message}
            />
          )}
        />
      </form>
    </Modal>
  );
}

export default ModalNewLocation;
